import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, fromEvent, map, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CountriesResponse } from '@app/shared/models/country';

const REGION_CODES = {
  CARIBBEAN: '029',
  LATIN_AMERICA: '419',
  WORLD: '001',
};

@Component({
  selector: 'flow-form-select-country',
  templateUrl: './form-select-country.component.html',
  styleUrls: ['./form-select-country.component.scss'],
})
export class FormSelectCountryComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('inputElement') inputElement!: ElementRef;
  private destroy$ = new Subject<void>();

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() options: CountriesResponse[] = [];
  @Input() isDisabled: boolean;
  @Input() multiple: boolean = false;
  @Input() required: boolean = false;
  @Input() selectAll: boolean = true;

  value: any;
  searchText: string = '';
  filteredOptions: CountriesResponse[] = [];
  language: string;
  selectAllClicked: boolean = false;
  isAllSelected: boolean = false;

  constructor(
    @Optional() @Self() protected control: NgControl,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngAfterViewInit() {
    fromEvent(this.inputElement.nativeElement, 'keyup')
      .pipe(
        map(() => this.inputElement.nativeElement.value),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(val => {
        this.onSearch(val);
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options?.length) {
      const source = changes['options'].currentValue;
      // Exclude regions that are not countries.
      // The .NET library we use for countries includes these.
      this.filteredOptions = source
        .map(opt => {
          return {
            ...opt,
            filtered: true,
          };
        })
        .filter((c: CountriesResponse) => !this.filterRegions(c.alpha2Code));
    }
  }

  onChange = (value: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  toggleSelectAll(): void {
    const optionIds = this.options.map(o => o.alpha2Code);
    const fullSelection = optionIds;

    if (this.isAllSelected) {
      this.value = [];
      this.selectAllClicked = false;
    } else {
      this.selectAllClicked = true;
      this.value = fullSelection;
    }

    this.isAllSelected = !this.isAllSelected;

    this.onSelectChange(this.value);
  }

  onSearch(term: string) {
    this.filteredOptions = this.options
      .filter((c: CountriesResponse) => c.alpha2Code !== '029' && c.alpha2Code !== '419' && c.alpha2Code !== '001')
      .map(opt => {
        const isMatch = opt.name.toLowerCase().includes(term.toLowerCase());
        return {
          ...opt,
          name: opt.name,
          filtered: isMatch,
        };
      });
  }

  onSelectChange(value: any) {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  clearSearchText(): void {
    this.inputElement.nativeElement.value = '';
    this.onSearch('');
    this.cdr.detectChanges();
  }

  filterRegions(alpha2Code: string): boolean {
    return Object.values(REGION_CODES).includes(alpha2Code);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
